import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getModelCoa, updateModelCoa } from 'api/aid-models/aid-coa.api';

type ModelId = string | number;
type CoaId = string | number;

interface UseModelCoaParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface UpdateCoaVariables {
  userId: string;
  modelId: ModelId;
  coaId: CoaId;
  data: unknown;
}

export const aidCoaKeys = {
  all: ['aidCoa'] as const,
  model: (modelId: ModelId): readonly unknown[] => [
    ...aidCoaKeys.all,
    'model',
    modelId,
  ],
};

export const useModelCoa = ({
  userId,
  modelId,
  enabled = true,
}: UseModelCoaParams) =>
  useQuery({
    queryKey: aidCoaKeys.model(modelId),
    queryFn: () => getModelCoa(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
    staleTime: 0,
  });

export const useUpdateModelCoa = () =>
  useMutation({
    mutationFn: ({ userId, modelId, coaId, data }: UpdateCoaVariables) =>
      updateModelCoa(userId, modelId, coaId, data),
  });

export const useInvalidateModelCoa = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (modelId: ModelId) =>
      queryClient.invalidateQueries({
        queryKey: aidCoaKeys.model(modelId),
      }),
    [queryClient]
  );
};
